import React, { useState, useRef, useEffect } from "react";
import styles from "./CustomDropdown.module.scss";

/**
 * Reusable custom dropdown matching the certificate upload dropdown UI.
 * Accepts options and works as a form control (onChange/onBlur with event-like target).
 *
 * @param {Array} options - List of { id, value } or { id, name } for display
 * @param {string|number} value - Current selected option id
 * @param {function} onChange - Called with { target: { getAttribute, value } } for form util
 * @param {function} onBlur - Optional blur handler
 * @param {string} name - Form field name (e.g. "form.sourceOfKnowledge")
 * @param {string} placeholder - Text when nothing selected
 * @param {boolean} required - Renders hidden required input for validation
 * @param {boolean} disabled - Disables the trigger
 * @param {string} title - Optional title attribute
 * @param {string} className - Optional wrapper class
 */
const CustomDropdown = ({
  options = [],
  value,
  onChange,
  onBlur,
  name,
  placeholder = "Select…",
  required = false,
  disabled = false,
  title,
  className = "",
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const hiddenInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find(
    (opt) => String(opt.id) === String(value)
  );
  const displayLabel = selectedOption
    ? selectedOption.value ?? selectedOption.name
    : placeholder;

  const makeSyntheticTarget = (val) => ({
    getAttribute: (attr) =>
      attr === "name" ? name : attr === "type" ? null : undefined,
    value: val,
    name,
    classList: {
      contains: () => false,
      remove: () => {},
      add: () => {},
    },
  });

  const handleSelect = (selectedId) => {
    const val = selectedId === undefined || selectedId === null ? "" : String(selectedId);
    onChange({ target: makeSyntheticTarget(val) });
    setDropdownOpen(false);
    if (onBlur && hiddenInputRef.current) {
      setTimeout(() => onBlur({ target: hiddenInputRef.current }), 0);
    }
  };

  return (
    <div className={`${styles.CustomDropdown} ${className}`.trim()}>
      {required && (
        <input
          type="hidden"
          ref={hiddenInputRef}
          name={name}
          value={value == null ? "" : value}
          required={required}
        />
      )}
      <div
        className={`certificate-dropdown certificate-upload-select ${
          disabled ? "cert-disabled" : ""
        }`}
        ref={dropdownRef}
      >
        <button
          type="button"
          className="dropdown-trigger"
          onClick={() => !disabled && setDropdownOpen((o) => !o)}
          disabled={disabled}
          title={title}
        >
          <span className="dropdown-label">{displayLabel}</span>
          <span className="dropdown-arrow">
            {dropdownOpen ? "▲" : "▼"}
          </span>
        </button>
        {dropdownOpen && (
          <ul className="dropdown-options">
            <li>
              <button
                type="button"
                className="dropdown-option"
                onClick={() => handleSelect(undefined)}
              >
                {placeholder}
              </button>
            </li>
            {options.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  className="dropdown-option"
                  onClick={() => handleSelect(option.id)}
                >
                  {option.value ?? option.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CustomDropdown;
